import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { connectScreenSize, ScreenSizeProps } from 'react-screen-size';
import { SvgIcon } from '@material-ui/core';
import { CancelRounded } from '@material-ui/icons';
import uuidv4 from 'uuid';
import Button from '../../atoms/buttons/Button';
import borders from '../../theme/borders';
import ModalWithPattern from '../../components/common/modals/ModalWithPattern';
import { IsMobileViewPort } from '../../utils/helpers';
import PriceInput from '../../components/pages/Offers/PriceInput'; //adjustStylePerDigit
import { CurrencyTip } from '../../components/pages/Offers/common';
import { getDefaultOfferAmount } from '../../utils/offers';
import WYPButtonIcon from '../../atoms/icons/WYPButtonIcon';
import { DashlineSeparator } from '../../components/join/styledElements';
import CardList from '../CardList';
import { defaultOfferAmount, renderAvatar } from '../../common';
import Card from '../../atoms/Card';
import { counterModalCustomAmount } from '../../actions/profileTrackAction';
import {
  ButtonDiv,
  CloseBtn,
  ContainerDiv,
  CurrencyLabelSpan,
  HeaderDiv,
  LogoIconWrapper,
  OfferTextInputWrapper,
  PriceContainer,
  ProfileCardsWrapper,
  Subtitle,
  Subtitle2,
  Title,
  adjustStylePerDigit,
  cardMarginForOddCard,
  closeButton,
  desktopContentStyle,
  mobileContentStyle,
  mobileOverlayStyle,
  inputPropsStyle,
  cardMarginForEvenCard,
} from './quickOfferModalStyle';
import OfferSuccess from '../../components/pages/Offers/OfferSuccess';
import WhatIsAnOfferModal from '../../components/common/modals/WhatIsAnOfferModal';
import { IProfile } from '../../types/common';
import WYPIcon from '../../atoms/icons/WYPIcon';
import useMandatoryWall from '../../hooks/useMandatoryWall';

interface QuickOfferModalProps {
  screenSizeIsMobile: boolean;
  screenSizeIsSmall: boolean;
  profile: IProfile;
  isModalOpen: boolean;
  offer: any;
  onClose: () => void;
  clearErrors: any;
  userProfiles: any;
  postQuickOffer: (formData: { accounts: string[]; price: number }) => any;
  setIsQuickOfferModalOpen: (isQuickOfferModalOpen?: boolean) => void;
}

const LogoIcon = () => {
  return <WYPIcon />;
};

const offerMinMaxVal = (offer, type, defaultAmount) => {
  const offerVal = offer && type === 'min' ? offer.data.min_counter_price : offer && offer.data.max_counter_price;

  return offer ? offerVal : defaultAmount;
};

const trackFocus = (ref, actionMethod) => {
  if (ref && ref.current) {
    ref.current.addEventListener('click', e => actionMethod(e), false);
  }

  return () => {
    ref.current.removeEventListener('click', () => null, false);
  };
};

const QuickOfferModal: FC<QuickOfferModalProps> = ({
  screenSizeIsMobile,
  screenSizeIsSmall,
  profile,
  isModalOpen,
  offer,
  clearErrors,
  onClose,
  userProfiles,
  postQuickOffer,
  setIsQuickOfferModalOpen,
}): ReactElement => {
  const isAttractiveUser = profile?.profile?.data?.account_type === 'Attractive';
  const currencyInitial = profile?.currency?.data?.initial;
  const currencyLabelUnicode =
    profile?.currency?.data?.label_unicode && String.fromCharCode(profile.currency.data.label_unicode);
  const defaultOfferPrice = getDefaultOfferAmount();
  const [defaultValue] = useState('');
  const [offerPrice, setOfferPrice] = useState(defaultOfferPrice);
  const [hasErrors, setHasErrors] = useState(false);
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
  const [selectedHashIds, setSelectedHashIds] = useState([]);
  const [offerSendingLoader, setOfferSendingLoader] = useState(false);
  const [showOfferSuccessModal, setShowOfferSuccessModal] = useState(false);
  const sendOfferBtn = useRef(null);
  const priceInput = useRef(null);
  const checkBoxRef = useRef([]);

  const { showMandatoryWall } = useMandatoryWall();

  useEffect(() => {
    clearErrors();
    trackFocus(priceInput, counterModalCustomAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userProfiles.length > 0) {
      const hashIds = userProfiles.map(item => item.hash_id);

      setSelectedHashIds(hashIds);
    }
  }, [userProfiles]);

  useEffect(() => {
    setMaxValue(offerMinMaxVal(offer, 'max', defaultOfferAmount.maxValue));
    setMinValue(offerMinMaxVal(offer, 'min', defaultOfferAmount.minValue));
  }, [offer]);

  const colorType = (price, hasErrors) => {
    switch (price) {
      case true:
        return 'default';
      case false:
        return !hasErrors ? 'brand' : 'alert';
      default:
        return;
    }
  };

  const updateOfferPrice = (val, error) => {
    if (!error) {
      clearErrors();
    }
    setOfferPrice(val);
    setHasErrors(error || false);
  };

  const validateTextInput = e => {
    const Key = e.key;
    const regex = /[0-9]/;

    if (!regex.test(Key)) {
      if (e.preventDefault) e.preventDefault();
    }
  };

  const inputOnChange = e => {
    const inputVal = Number(e.target.value);
    const minValueNum = Number(minValue);
    const maxValueNum = Number(maxValue);
    const maxLength = maxValueNum.toString().length;

    if (e.target.value.length <= maxLength) {
      const isError = inputVal < minValueNum || inputVal > maxValueNum;

      updateOfferPrice(inputVal, isError);
    } else {
      e.preventDefault();
    }
  };

  const handleSelectedUsers = (hashId: string) => {
    let updatedHashIds = JSON.parse(JSON.stringify(selectedHashIds));

    if (!updatedHashIds.includes(hashId)) {
      updatedHashIds.push(hashId);
    } else {
      updatedHashIds = updatedHashIds.filter(function (id: string) {
        return id != hashId;
      });
    }
    setSelectedHashIds(updatedHashIds);
  };

  const renderUserProfileResults = (
    profile: any,
    index: number,
    screenSizeIsMobile: boolean,
    screenSizeIsSmall: boolean
  ) => {
    try {
      const age = profile?.profile?.data?.age;
      const avatar = profile?.photo?.data?.length && renderAvatar(profile.photo.data[0].urls);
      const city = profile.profile.data.city;
      const username = profile?.username || '';
      const hashId = profile.hash_id || username;
      const photoCount = parseInt(profile.profile.data.approved_public_photos_count, 10);
      const privatePhotoCount = profile.profile.data.private_photos_count
        ? parseInt(profile.profile.data.private_photos_count, 10)
        : 0;
      const online = profile.profile.data.online;
      const userVerified = profile.id_verification?.data?.status === 'approved';
      const isSelfieVerified = profile?.profile?.data?.selfie_verified;
      const cardMargin = screenSizeIsMobile
        ? index % 2 == 0
          ? '3.5px 3.5px 3.5px 7px'
          : '3.5px 7px 3.5px 3.5px'
        : '2.18%';
      const cardMarginForSmallXS = index % 2 == 0 ? cardMarginForEvenCard : cardMarginForOddCard;
      const isChecked = (selectedHashIds.length > 0 && selectedHashIds.includes(hashId)) || false;

      return (
        <Card
          id={`quickoffer-user-card-${hashId}`}
          onClick={handleSelectedUsers}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          key={uuidv4()}
          age={age}
          city={city}
          username={username}
          hashId={hashId}
          photoCount={photoCount}
          privatePhotoCount={privatePhotoCount}
          online={online}
          cardWidth={screenSizeIsMobile ? '46.9%' : '45.2%'}
          cardMargin={cardMargin}
          imgUrl={avatar}
          isVerified={userVerified}
          screenIsMobile={screenSizeIsMobile || screenSizeIsSmall}
          link={'#'}
          checkBoxRef={checkBoxRef}
          index={index}
          isLinkClickable={false}
          showSelectUserCheckbox={true}
          isChecked={isChecked}
          customCardStyle={cardMarginForSmallXS}
          isSelfieVerified={isSelfieVerified}
        />
      );
    } catch (e) {
      console.error('error', e);
      return null;
    }
  };

  const handleSendOffer = e => {
    e.preventDefault();
    showMandatoryWall(
      () => handleQuickOfferSubmit(),
      () => setOfferSendingLoader(false),
      'Make Offer'
    );
  };

  const handleQuickOfferSubmit = () => {
    try {
      const formData = {
        accounts: selectedHashIds,
        price: offerPrice,
      };

      setOfferSendingLoader(true);
      postQuickOffer(formData)
        .then(() => {
          sessionStorage.setItem('quickOfferSubmitted', 'true');
          setIsQuickOfferModalOpen(false);
          setOfferSendingLoader(false);
          setShowOfferSuccessModal(true);
        })
        .catch(() => {
          setOfferSendingLoader(false);
          setHasErrors(true);
        });
    } catch (error) {
      setOfferSendingLoader(false);
      console.log('error', error);
    }
  };

  const handleSuccessModalClose = () => {
    sessionStorage.removeItem('hasAddedPhoto');
    setShowOfferSuccessModal(false);
  };

  return (
    <>
      <ModalWithPattern
        dataTestID="quick-offer-modal"
        isModalOpen={isModalOpen}
        modalContentLabel="Quick Offer Modal"
        closeOnOverlayClick
        closeRequest={onClose}
        closeBtnClassName="quick-offer-close"
        closeBtnDataTestId="quick-offer-close"
        onCloseClick={onClose}
        withoutBgPattern={true}
        customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
        customContentStyle={IsMobileViewPort ? mobileContentStyle : desktopContentStyle}
        customContentBodyStyle={{ padding: 0, background: 'none' }}
        hideCloseBtn={true}
      >
        <ContainerDiv>
          <CloseBtn onClick={onClose}>
            <SvgIcon component={CancelRounded} style={closeButton} />
          </CloseBtn>
          <LogoIconWrapper>
            <LogoIcon />
          </LogoIconWrapper>
          <HeaderDiv>
            <Title>{`Let's go!`}</Title>
            <Subtitle>Nearby members are eager to date you.</Subtitle>
            <Subtitle2>{`Set your ${
              isAttractiveUser ? 'Offer Request' : 'Offer'
            }, choose one (or all), send, and start planning your next date!`}</Subtitle2>
          </HeaderDiv>
          <OfferTextInputWrapper>
            <Subtitle>{isAttractiveUser ? 'Your offer request amount' : 'Your offer amount'}:</Subtitle>
            <PriceContainer hasErrors={hasErrors}>
              <CurrencyTip
                offerPrice={colorType(offerPrice, hasErrors)}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
                }}
              >
                <CurrencyLabelSpan>
                  {currencyInitial}
                  {currencyLabelUnicode}
                </CurrencyLabelSpan>

                <PriceInput
                  inputRef={priceInput}
                  data-test-id="offer-price-input"
                  hasErrors={hasErrors}
                  value={offerPrice}
                  onChange={inputOnChange}
                  validateTextFn={validateTextInput}
                  placeholder={defaultValue}
                  inputProps={{
                    style: {
                      ...inputPropsStyle,
                      marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
                    },
                  }}
                  minWidth={100}
                />
              </CurrencyTip>
            </PriceContainer>
            <WhatIsAnOfferModal isAttractiveUser={isAttractiveUser} />
          </OfferTextInputWrapper>
          <ProfileCardsWrapper>
            <CardList
              isMobile={screenSizeIsMobile}
              listMargin={screenSizeIsMobile ? '0px -20px 0px -20px' : '0px 30px'}
            >
              {userProfiles.length > 0 &&
                userProfiles.map((profile, index) => {
                  return renderUserProfileResults(profile, index, screenSizeIsMobile, screenSizeIsSmall);
                })}
            </CardList>
          </ProfileCardsWrapper>
          {IsMobileViewPort ? null : <DashlineSeparator style={{ width: '55%', marginBottom: 0 }} />}
          <ButtonDiv>
            <Button
              id={'quick-offer-btn'}
              buttonRef={sendOfferBtn}
              disabled={selectedHashIds.length < 1 || hasErrors || offerSendingLoader}
              buttonType="error"
              onClick={handleSendOffer}
              type="button"
              customStyle={{ width: '195px' }}
            >
              <WYPButtonIcon style={{ marginRight: 6 }} />
              {offerSendingLoader ? 'Sending...' : isAttractiveUser ? 'Send Requests' : 'Send Offers'}
            </Button>
            <Button
              buttonType="chromeless"
              data-test-id="s"
              customStyle={{
                marginTop: '20px',
                fontSize: 12,
                lineHeight: '17px',
                fontWeight: 400,
                padding: '4px 0px',
                color: borders.light.secondary,
                height: '20px',
              }}
              onClick={onClose}
            >
              Not right now
            </Button>
          </ButtonDiv>
        </ContainerDiv>
      </ModalWithPattern>
      <OfferSuccess
        isModalOpen={showOfferSuccessModal}
        onClose={handleSuccessModalClose}
        offerAmount={''}
        avatarSrc={''}
        username={''}
        buttonID="quickOffersSend"
        isCounter={false}
        isRevised={false}
        isSuggest={false}
        mainTitle={isAttractiveUser ? 'Offer Requests Sent!' : 'Offers Sent!'}
        subTitleOne={isAttractiveUser ? 'Your offer requests have been sent!' : 'Your offers have been sent!'}
        subTitleTwo={'Check your Offers page to see who’s accepted.'}
        customHeaderStyle={{ marginTop: 20 }}
        customTitleStyle={{ marginTop: 18 }}
      />
    </>
  );
};

const mapScreenToProps = (screenSize: ScreenSizeProps) => {
  return {
    screenSizeIsMobile: screenSize.mobile,
    screenSizeIsSmall: screenSize.small,
  };
};

export default connectScreenSize(mapScreenToProps)(QuickOfferModal);
